/**
 * GA4 / Google Ads e-commerce events (client side).
 *
 * The storefront already loads gtag.js with the GA4 measurement id (root.tsx,
 * only after analytics consent). Until 2026-09-04 nothing but page_views was
 * sent, so Google Ads had no conversions to import. These helpers mirror the
 * Meta Pixel events one-to-one so the two platforms see the same funnel:
 *
 *   view_item · add_to_cart · begin_checkout · purchase · generate_lead
 *   (a Messenger / Zalo / LINE chat click — 90 % of revenue is DM-driven,
 *   so "chat started" is a key event for bidding, not just purchases).
 *
 * When `window.ENV.GOOGLE_ADS_ID` (AW-XXXXXXXXX) is set, the same events
 * are also sent to the Ads tag with retail dynamic-remarketing parameters so
 * Performance Max / remarketing can use them. Conversions themselves are
 * imported into Ads from the GA4 key events (no per-conversion labels to
 * keep in sync). Everything is fire-and-forget and never throws.
 */

type Gtag = (...args: unknown[]) => void;

export interface GaItem {
  item_id: string;
  item_name: string;
  price?: number;
  quantity?: number;
  item_variant?: string;
  item_category?: string;
  item_brand?: string;
}

function gtag(): Gtag | null {
  if (typeof window === "undefined") return null;
  const g = (window as unknown as { gtag?: Gtag }).gtag;
  return typeof g === "function" ? g : null;
}

function adsId(): string | null {
  if (typeof window === "undefined") return null;
  const env = (window as unknown as { ENV?: { GOOGLE_ADS_ID?: string } }).ENV;
  const id = env?.GOOGLE_ADS_ID;
  return id && /^AW-\d+$/.test(id) ? id : null;
}

function send(name: string, params: Record<string, unknown>) {
  const g = gtag();
  if (!g) return;
  try {
    g("event", name, params);
    const aw = adsId();
    if (aw && typeof params.value === "number") {
      // Retail dynamic remarketing parameters (Google's "retail" vertical).
      const items = Array.isArray(params.items) ? (params.items as GaItem[]) : [];
      g("event", name, {
        send_to: aw,
        value: params.value,
        currency: params.currency,
        items: items.map((i) => ({ id: i.item_id, google_business_vertical: "retail" })),
      });
    }
  } catch {
    // analytics must never break the shop
  }
}

export function gaViewItem(item: GaItem, currency: string) {
  send("view_item", { currency, value: item.price ?? 0, items: [{ ...item, quantity: 1 }] });
}

export function gaAddToCart(item: GaItem, currency: string) {
  const qty = item.quantity ?? 1;
  send("add_to_cart", { currency, value: (item.price ?? 0) * qty, items: [item] });
}

export function gaBeginCheckout(items: GaItem[], currency: string, value: number) {
  send("begin_checkout", { currency, value, items });
}

export function gaPurchase(input: {
  transactionId: string;
  value: number;
  currency: string;
  shipping?: number;
  items?: GaItem[];
}) {
  send("purchase", {
    transaction_id: input.transactionId,
    value: input.value,
    currency: input.currency,
    shipping: input.shipping ?? 0,
    items: input.items ?? [],
  });
}

/** A chat click (Messenger / Zalo / LINE) — the DM funnel's first step. */
export function gaChatLead(channel: "messenger" | "zalo" | "line", context?: { productId?: string; productTitle?: string }) {
  send("generate_lead", {
    currency: "VND",
    value: 0,
    lead_channel: channel,
    ...(context?.productId ? { item_id: context.productId, item_name: context.productTitle } : {}),
  });
}
